import { DraggableAttributes } from "@dnd-kit/core";
import { ChatBotSettingsFormValues } from "@jugl-web/domain-resources/chat-bot";
import { MAX_CTA_LENGTH } from "@jugl-web/domain-resources/chat-bot/consts";
import { TextInput } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { FC } from "react";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { AppearanceSettingsTabShell } from "../AppearanceSettingsTabShell";
import { Separator } from "../../../../components/Separator";
import { TabHeader } from "../../../../components/TabHeader";
import {
  DraggableList,
  DragHandle,
  SortableItem,
} from "../../../../components/DraggableList";
import { CTAVisualSection } from "../../../../components/CTAVisualSection";

export const CTATabContent = () => {
  const { t } = useTranslations();

  return (
    <AppearanceSettingsTabShell tabId="cta" previewMode="ctas">
      <TabHeader
        title={t({
          id: "client-support-page.chat-starters-tab-title",
          defaultMessage: "Chat Starters",
        })}
        subtitle={t({
          id: "client-support-page.chat-starters-tab-subtitle",
          defaultMessage:
            "Engage visitors with 1–2 short chat starters to encourage visitors to start a conversation",
        })}
      />
      <Separator className="my-8" />
      <CTATabContentInner />
    </AppearanceSettingsTabShell>
  );
};

const CTATabContentInner: FC = () => {
  const { watch, control, setValue } =
    useFormContext<ChatBotSettingsFormValues>();

  const { fields, move } = useFieldArray<ChatBotSettingsFormValues>({
    control,
    name: "ctas",
  });

  const ctas = watch("ctas") || [];
  const filledCTAsCount = ctas.filter((cta) => cta.value.trim() !== "").length;
  const isDragDisabled = filledCTAsCount < 2;

  const ctaVisual = watch("ctaVisual");

  return (
    <div className="flex flex-col gap-6">
      <DraggableList
        items={fields}
        getId={(item) => item.id}
        onReorder={move}
        isDragDisabled={isDragDisabled}
        renderItem={(item, index, disabled) => (
          <SortableItem key={item.id} id={item.id} disabled={disabled}>
            {({ dragHandleProps, isDragging }) => (
              <CTAInput
                index={index}
                disabled={disabled}
                isDragging={isDragging}
                dragHandleProps={dragHandleProps}
              />
            )}
          </SortableItem>
        )}
      />
      <Separator />
      <CTAVisualSection
        value={ctaVisual}
        onChange={(value) =>
          setValue("ctaVisual", value, { shouldDirty: true })
        }
      />
    </div>
  );
};

interface CTAInputProps {
  index: number;
  disabled: boolean;
  isDragging: boolean;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners: SyntheticListenerMap | undefined;
  };
}

const CTAInput: FC<CTAInputProps> = ({
  index,
  disabled,
  isDragging,
  dragHandleProps,
}) => {
  const { t } = useTranslations();
  const { control } = useFormContext<ChatBotSettingsFormValues>();

  return (
    <div
      className="flex items-center gap-2"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <Controller
        control={control}
        name={`ctas.${index}.value`}
        render={({ field: { onChange, ...props } }) => (
          <TextInput
            labelPosition="border-overlap"
            maxLength={MAX_CTA_LENGTH}
            lengthIndicator={{
              current: props.value.length,
              max: MAX_CTA_LENGTH,
            }}
            label={t(
              {
                id: "client-support-page.starter-number",
                defaultMessage: "Starter {number}",
              },
              { number: index + 1 }
            )}
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
            classNames={{
              wrapperClassName: "w-full",
            }}
            onChange={(e) => onChange(e.target.value.trimStart())}
            {...props}
          />
        )}
      />
      <DragHandle disabled={disabled} dragHandleProps={dragHandleProps} />
    </div>
  );
};
